import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useConfirmApi } from 'utils/api/ApiHooks';
import { useIsSuperAdmin } from 'utils/models/User';
import {
  formatWallClockAsNaiveIsoString,
  getBrowserTimezone,
  getTimezoneOption,
  getWallClockInTimeZone,
  type TimezoneOption,
} from 'utils/util/timezone';
import {
  AdvancedCurrentPeopleFilterRoleUserApiComponent,
  AdvancedCurrentPeopleRoleUserBelowCOCAdminableFilterApiComponent,
} from 'views/Widgets/Filters/AdvancedSpecializedFilter/highOrder';
import { type ApiGroup } from 'views/Widgets/Filters/AdvancedSpecializedFilter/types';
import { INPUT_TYPES } from 'views/Widgets/Inputs/ValidatedInputTypes';
import ModalEditor from 'views/Widgets/Modals/ModalEditor';
import {
  CADENCE_OPTIONS,
  type CustomSurveyData,
  MILESTONE_FIELD_OPTIONS,
  NAME_DESCRIPTION_INPUTS,
  SCHEDULE_INPUTS,
  type ScheduledParticipant,
  SURVEY_TYPES,
  validateCustomSurveySettings,
} from './consts';
import ScheduledParticipantsField from './ScheduledParticipantsField';
import TargetFilterLabel from './TargetFilterLabel';

interface Props {
  isOpen: boolean;
  toggle: () => void;
  onSave: (data: Partial<CustomSurveyData>) => void;
  existingSurvey?: CustomSurveyData | null;
}

const EMPTY_FORM = {};

const CustomSurveyEditorModal: FC<Props> = ({
  isOpen,
  toggle,
  onSave,
  existingSurvey,
}) => {
  const { formatMessage } = useIntl();
  const isSuperAdmin = useIsSuperAdmin();
  // Superusers have no direct reports or HR-admin scope of their own to
  // speak of - they should see the whole org, not "nobody"
  // (BELOW_CHAIN_OF_COMMAND_OR_ADMINABLE resolves to ~0 people for them).
  // System admins are deliberately excluded - they can't reach this modal
  // at all now (see can_manage_custom_surveys), so they're not a case this
  // needs to handle.
  const hasUnrestrictedAccess = isSuperAdmin;

  const [formObj, setFormObj] = useState<Record<string, unknown>>(EMPTY_FORM);

  // Live preview of the HR administration scope that will be frozen onto the
  // survey as admin_target_audience at creation time (see
  // CustomSurveySerializer.create()). Read-only; there's no survey yet to
  // read a frozen value from.
  const { data: adminScopeData } = useConfirmApi<{
    admin_target_audience?: ApiGroup;
  }>({
    method: 'GET',
    url: 'custom-survey/surveys/admin-target-audience',
    disabled: !isOpen,
  });
  const adminTargetAudience = adminScopeData?.admin_target_audience;

  useEffect(() => {
    if (!isOpen) return;
    if (existingSurvey) {
      setFormObj({
        name: existingSurvey.name || '',
        description: existingSurvey.description || '',
        survey_type: existingSurvey.survey_type || '',
        cadence: existingSurvey.cadence ?? null,
        milestone_days_offset: existingSurvey.milestone_days_offset ?? null,
        milestone_field: existingSurvey.milestone_field ?? null,
        // Shown as the stored instant's wall clock in the survey's *own*
        // timezone, not the viewer's, so the picker displays what was meant
        // - matching CustomSurveyBasicsTab's seed rather than handing
        // DatePicker a raw UTC string for it to reinterpret locally.
        scheduled_time: existingSurvey.scheduled_time
          ? (getWallClockInTimeZone(
              new Date(existingSurvey.scheduled_time),
              existingSurvey.scheduled_timezone || getBrowserTimezone()
            ) as unknown as string)
          : null,
        scheduled_timezone: getTimezoneOption(
          existingSurvey.scheduled_timezone || getBrowserTimezone()
        ) as unknown as string,
        days_to_respond: existingSurvey.days_to_respond ?? null,
        scheduled_participants: (
          existingSurvey.scheduled_participants ?? []
        ).map((participant) => ({
          ...participant,
          scheduled_time: getWallClockInTimeZone(
            new Date(participant.scheduled_time),
            participant.scheduled_timezone
          ) as unknown as string,
        })),
        target_filter:
          existingSurvey.target_filter &&
          Object.keys(existingSurvey.target_filter).length > 0
            ? (existingSurvey.target_filter as unknown as ApiGroup)
            : undefined,
      });
    } else {
      setFormObj({
        ...EMPTY_FORM,
        // Pre-selected so the picker is never empty when ONE_TIME or PERIODIC
        // is chosen.
        scheduled_timezone: getTimezoneOption(getBrowserTimezone()),
      });
    }
  }, [isOpen, existingSurvey]);

  const surveyTypes = useMemo(
    () => SURVEY_TYPES(formatMessage),
    [formatMessage]
  );
  const cadenceOptions = useMemo(
    () => CADENCE_OPTIONS(formatMessage),
    [formatMessage]
  );
  const milestoneFieldOptions = useMemo(
    () => MILESTONE_FIELD_OPTIONS(formatMessage),
    [formatMessage]
  );

  const inputs = useMemo(
    () => [
      ...NAME_DESCRIPTION_INPUTS(formatMessage),
      {
        name: 'survey_type',
        type: INPUT_TYPES.DROPDOWN,
        label: formatMessage({
          id: 'app.views.administration.custom_surveys.surveys.editor.type_label',
          defaultMessage: 'When do you want to send this survey?',
        }),
        objects: surveyTypes,
        required: true,
        clearable: false,
        selectText: formatMessage({
          id: 'app.views.administration.custom_surveys.surveys.editor.type_placeholder',
          defaultMessage: 'Select a survey type',
        }),
      },
      ...(formObj.survey_type === 'PER_PERSON'
        ? [
            {
              name: 'scheduled_participants',
              type: INPUT_TYPES.CUSTOM_COMPONENT,
              component: ScheduledParticipantsField,
              targetFilter: formObj.target_filter ?? null,
            },
          ]
        : []),
      ...(formObj.survey_type === 'PERIODIC'
        ? [
            {
              name: 'cadence',
              type: INPUT_TYPES.DROPDOWN,
              label: formatMessage({
                id: 'app.views.administration.custom_surveys.surveys.editor.cadence_label',
                defaultMessage: 'How often should this survey be sent?',
              }),
              objects: cadenceOptions,
              required: true,
              clearable: false,
              selectText: formatMessage({
                id: 'app.views.administration.custom_surveys.surveys.editor.cadence_placeholder',
                defaultMessage: 'Select cadence',
              }),
            },
            ...SCHEDULE_INPUTS(formatMessage, {
              dateLabel: formatMessage({
                id: 'app.views.administration.custom_surveys.surveys.editor.first_scheduled_time_label',
                defaultMessage: 'When should the first survey be sent?',
              }),
              minDate: 'today',
            }),
          ]
        : []),
      ...(formObj.survey_type === 'ONE_TIME'
        ? SCHEDULE_INPUTS(formatMessage, {
            dateLabel: formatMessage({
              id: 'app.views.administration.custom_surveys.surveys.editor.scheduled_time_label',
              defaultMessage: 'When should this survey be sent?',
            }),
            minDate: 'today',
          })
        : []),
      ...(formObj.survey_type === 'MILESTONE'
        ? [
            {
              name: 'milestone_field',
              type: INPUT_TYPES.DROPDOWN,
              label: formatMessage({
                id: 'app.views.administration.custom_surveys.surveys.editor.milestone_field_label',
                defaultMessage:
                  'Which milestone date do you want to anchor on?',
              }),
              objects: milestoneFieldOptions,
              required: true,
              clearable: false,
              selectText: formatMessage({
                id: 'app.views.administration.custom_surveys.surveys.editor.milestone_field_placeholder',
                defaultMessage: 'Select milestone date',
              }),
            },
            {
              name: 'milestone_days_offset',
              type: INPUT_TYPES.TEXT,
              inputType: 'number',
              label: formatMessage({
                id: 'app.views.administration.custom_surveys.surveys.editor.milestone_days_offset_label',
                defaultMessage: 'How many days after this milestone?',
              }),
              placeholder: formatMessage({
                id: 'app.views.administration.custom_surveys.surveys.editor.milestone_days_offset_placeholder',
                defaultMessage: 'e.g. 30',
              }),
              minValue: 0,
              required: true,
            },
          ]
        : []),
      {
        name: 'days_to_respond',
        type: INPUT_TYPES.TEXT,
        inputType: 'number',
        label: formatMessage({
          id: 'app.views.administration.custom_surveys.surveys.editor.days_to_respond_label',
          defaultMessage:
            'How many days should each participant have to respond?',
        }),
        placeholder: formatMessage({
          id: 'app.views.administration.custom_surveys.surveys.editor.days_to_respond_placeholder',
          defaultMessage: 'e.g. 7',
        }),
        minValue: 1,
        required: true,
      },
      {
        name: 'target_filter',
        type: INPUT_TYPES.CUSTOM_COMPONENT,
        component: hasUnrestrictedAccess
          ? AdvancedCurrentPeopleFilterRoleUserApiComponent
          : AdvancedCurrentPeopleRoleUserBelowCOCAdminableFilterApiComponent,
        label: <TargetFilterLabel adminTargetAudience={adminTargetAudience} />,
      },
    ],
    [
      formObj.survey_type,
      formObj.target_filter,
      surveyTypes,
      cadenceOptions,
      milestoneFieldOptions,
      formatMessage,
      adminTargetAudience,
      hasUnrestrictedAccess,
    ]
  );

  const handleChange = useCallback((newObj: Record<string, unknown>) => {
    setFormObj(newObj);
  }, []);

  const handleValidate = useCallback(
    (obj: Record<string, unknown>) =>
      validateCustomSurveySettings(obj, formatMessage),
    [formatMessage]
  );

  const transformObjectBeforeSubmit = useCallback(
    (data: Record<string, unknown>): Partial<CustomSurveyData> => {
      const isMilestone = data.survey_type === 'MILESTONE';
      const isPeriodic = data.survey_type === 'PERIODIC';
      const isOneTime = data.survey_type === 'ONE_TIME';
      const isPerPerson = data.survey_type === 'PER_PERSON';
      return {
        name: (data.name as string).trim(),
        description: (data.description as string)?.trim() || undefined,
        survey_type: data.survey_type as string,
        days_to_respond:
          data.days_to_respond != null && data.days_to_respond !== ''
            ? Number(data.days_to_respond)
            : null,
        cadence: isPeriodic ? ((data.cadence as string) ?? null) : null,
        milestone_days_offset:
          isMilestone && data.milestone_days_offset != null
            ? Number(data.milestone_days_offset)
            : null,
        milestone_field: isMilestone
          ? ((data.milestone_field as string) ?? null)
          : null,
        // A plain wall-clock string (no offset) - the backend pairs it with
        // scheduled_timezone to do the authoritative UTC conversion. Sent for
        // PERIODIC too, where it is the first send.
        scheduled_time:
          isOneTime || isPeriodic
            ? data.scheduled_time
              ? formatWallClockAsNaiveIsoString(
                  new Date(data.scheduled_time as string)
                )
              : null
            : null,
        // The timezone the user actually picked, unwrapped from the SELECT's
        // option object. Explicitly null for MILESTONE, which the DB
        // constraint requires it to be. Optional-chained because the picker is
        // clearable - only pre-seeded, not guaranteed.
        scheduled_timezone:
          isOneTime || isPeriodic
            ? ((data.scheduled_timezone as unknown as TimezoneOption)?.value ??
              null)
            : null,
        target_filter: (data.target_filter as Record<string, unknown>) ?? null,
        ...(isPerPerson
          ? {
              // Sent rows stay visible in the table but are left out of the
              // payload: the server refuses to retime or delete them anyway,
              // and omitting them stops a recipient whose record has since
              // changed from failing the audience check and locking the survey.
              scheduled_participants: (
                (data.scheduled_participants as ScheduledParticipant[]) ?? []
              )
                .filter((participant) => !participant.sent_at)
                .map((participant) => ({
                  person_id: participant.person_id,
                  scheduled_time: formatWallClockAsNaiveIsoString(
                    new Date(participant.scheduled_time)
                  ),
                  scheduled_timezone: participant.scheduled_timezone,
                })),
            }
          : {}),
      };
    },
    []
  );

  const title = existingSurvey
    ? formatMessage({
        id: 'app.views.administration.custom_surveys.surveys.editor.title.edit',
        defaultMessage: 'Edit lifecycle survey',
      })
    : formatMessage({
        id: 'app.views.administration.custom_surveys.surveys.editor.title.create',
        defaultMessage: 'Create lifecycle survey',
      });

  const submitText = existingSurvey
    ? formatMessage({
        id: 'app.views.administration.custom_surveys.surveys.editor.save',
        defaultMessage: 'Save',
      })
    : formatMessage({
        id: 'app.views.administration.custom_surveys.surveys.editor.create',
        defaultMessage: 'Create',
      });

  return (
    <ModalEditor
      isOpen={isOpen}
      toggle={toggle}
      title={title}
      object={formObj}
      inputs={inputs}
      onChange={handleChange}
      onValidate={handleValidate}
      transformObjectBeforeSubmit={transformObjectBeforeSubmit}
      submitText={submitText}
      disableUnsavedChangesPrompt={true}
      noValidate
      callback={onSave}
      size="lg"
    />
  );
};

export default React.memo(CustomSurveyEditorModal);
